import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  LinearProgress,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ExpandMore, Lightbulb, Save, Undo } from '@mui/icons-material';

import { IGwGFrage, IGwGPruefung } from '../../../Interfaces/IGwGPruefung';
import { useGwGPruefung } from '../../../hooks/reactQuerys/GwGPruefungQueries';
import { useAlert } from '../../../context/AlertContext';
import { ApiError } from '../../../hooks/useFetch';
import { GwGPflichtenListe } from './GwGPflichtenListe';

interface IProps {
  open: boolean;
  pruefung: IGwGPruefung;
  setPruefung: (pruefung: IGwGPruefung) => void;
  onClose: () => void;
}

/** Mehrfachauswahl kommt zeilenweise — so erwartet es der Server. */
const alsAntwort = (auswahl: string[]) => auswahl.join('\n');

const fehlertext = (error: unknown, ersatz: string) => {
  if (error instanceof ApiError) {
    const detail = (error.body as { error?: unknown } | null)?.error;

    if (typeof detail === 'string' && detail.trim() !== '') {
      return detail.replace(/^\d{3}\s[^:]+:\s*/, '');
    }
  }

  return ersatz;
};

/**
 * Die Prüfung führen — eine Frage nach der anderen.
 *
 * Bewusst ein Dialog und keine Seite: die Prüfung läuft „an einem Stück“,
 * und wer dabei im Vorgang herumklickt, verliert den Faden. Unterbrechen
 * geht trotzdem jederzeit — der Stand liegt auf dem Server, nicht im Fenster.
 */
export const GwGPruefungDialog: React.FC<IProps> = ({
  open,
  pruefung,
  setPruefung,
  onClose,
}) => {
  const { showAlert } = useAlert();
  const {
    antworten,
    zurueck,
    ergebnisAblegen,
    isPendingAntworten,
    isPendingErgebnis,
  } = useGwGPruefung();

  const [auswahl, setAuswahl] = useState<string[]>([]);
  const [freitext, setFreitext] = useState('');
  /** Wurde der Vorschlag unverändert übernommen? */
  const [vorschlagUebernommen, setVorschlagUebernommen] = useState(false);
  const [letzteFrage, setLetzteFrage] = useState<string | null>(null);

  const frage = pruefung.Frage;

  // Bei jeder neuen Frage den Vorschlag übernehmen — aber nur als Vorgabe im
  // Feld. Abgeschickt wird erst, wenn jemand auf „Weiter“ klickt.
  if (frage && frage.Key !== letzteFrage) {
    setLetzteFrage(frage.Key);
    setAuswahl(frage.Vorschlag ? frage.Vorschlag.Antwort.split('\n') : []);
    setFreitext('');
    setVorschlagUebernommen(Boolean(frage.Vorschlag));
  }

  const waehle = (wert: string, mehrfach: boolean) => {
    setVorschlagUebernommen(false);
    setAuswahl(previous =>
      mehrfach
        ? previous.includes(wert)
          ? previous.filter(eintrag => eintrag !== wert)
          : [...previous, wert]
        : [wert]
    );
  };

  async function weiter(aktuelleFrage: IGwGFrage) {
    // Eine Hinweisseite hat keine Antwort. Trotzdem ein leerer Text und
    // nicht `null`: die Schnittstelle lässt an dieser Stelle nur Text zu, und
    // `null` würde abgewiesen, ohne dass es jemandem auffällt.
    const antwort =
      aktuelleFrage.Art === 'Content'
        ? ''
        : aktuelleFrage.Art === 'Text'
          ? freitext.trim()
          : alsAntwort(auswahl);

    try {
      setPruefung(
        await antworten({
          idGwGCheck: pruefung.idGwGCheck,
          Answer: antwort,
          wasPrefilled: vorschlagUebernommen,
        })
      );
    } catch (error) {
      showAlert({
        text: fehlertext(error, 'Die Antwort konnte nicht gespeichert werden.'),
        severity: 'error',
      });
    }
  }

  async function zurueckZu(nodeKey: string) {
    try {
      setPruefung(
        await zurueck({ idGwGCheck: pruefung.idGwGCheck, NodeKey: nodeKey })
      );
      setLetzteFrage(null);
    } catch (error) {
      showAlert({
        text: fehlertext(error, 'Es konnte nicht zurückgegangen werden.'),
        severity: 'error',
      });
    }
  }

  async function legeErgebnisAb() {
    try {
      const abgelegt = await ergebnisAblegen(pruefung.idGwGCheck);
      showAlert({
        text: `Prüfungsergebnis im Ordner „${abgelegt.Ordner}“ abgelegt.`,
        severity: 'success',
      });
    } catch (error) {
      showAlert({
        text: fehlertext(error, 'Das Ergebnis konnte nicht abgelegt werden.'),
        severity: 'error',
      });
    }
  }

  const istBeantwortbar =
    !frage ||
    frage.Art === 'Content' ||
    (frage.Art === 'Text' ? freitext.trim() !== '' : auswahl.length > 0);

  const vorletzte = pruefung.AntwortArray.slice(-1)[0];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      /* Der Vorgang ist selbst ein Modal. Ohne eigene Ebene rendert dieser
         Dialog dahinter: sichtbar, aber nicht bedienbar — Klicks landen auf
         dem Hintergrund des äußeren Fensters. */
      sx={{ zIndex: theme => theme.zIndex.modal + 10 }}
      PaperProps={{ sx: { bgcolor: 'background.paper' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack
          direction='row'
          alignItems='center'
          spacing={1}
          sx={{ flexWrap: 'wrap' }}
        >
          <Typography variant='h6'>GwG-Prüfung</Typography>
          {pruefung.Schritt && (
            <Chip size='small' label={pruefung.Schritt} variant='outlined' />
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant='caption' color='text.secondary'>
            Katalogstand {pruefung.CatalogVersion}
          </Typography>
        </Stack>
      </DialogTitle>

      {isPendingAntworten && <LinearProgress />}

      <DialogContent dividers>
        {frage === null ? (
          <Alert severity='success' sx={{ mb: 2 }}>
            Die Prüfung ist abgeschlossen.
          </Alert>
        ) : (
          <>
            {frage.Bereich && (
              <Typography variant='overline' color='text.secondary'>
                {frage.Bereich}
              </Typography>
            )}

            {frage.TextArray.map((zeile, index) => (
              <Typography
                key={zeile}
                variant={index === 0 ? 'subtitle1' : 'body2'}
                sx={{ mb: 0.5, fontWeight: index === 0 ? 600 : 400 }}
              >
                {zeile}
              </Typography>
            ))}

            {frage.Vorschlag && (
              <Alert
                severity='info'
                icon={<Lightbulb fontSize='small' />}
                sx={{ mt: 1.5, mb: 1 }}
              >
                Vorschlag aus dem Vorgang:{' '}
                <strong>{frage.Vorschlag.Antwort.replace(/\n/g, ', ')}</strong>
                <br />
                <Typography variant='caption'>
                  {frage.Vorschlag.Begruendung} — bitte prüfen und bestätigen.
                </Typography>
              </Alert>
            )}

            {frage.Art === 'Text' ? (
              <TextField
                fullWidth
                multiline
                minRows={3}
                label='Begründung'
                value={freitext}
                onChange={event => setFreitext(event.target.value)}
                sx={{ mt: 2 }}
              />
            ) : frage.Art === 'MultiSelect' ? (
              <Box sx={{ mt: 1 }}>
                {frage.AntwortArray.map(antwort => (
                  <FormControlLabel
                    key={antwort}
                    sx={{ display: 'flex', mr: 0 }}
                    control={
                      <Checkbox
                        size='small'
                        checked={auswahl.includes(antwort)}
                        onChange={() => waehle(antwort, true)}
                      />
                    }
                    label={antwort}
                  />
                ))}
              </Box>
            ) : frage.AntwortArray.length > 0 ? (
              <RadioGroup
                sx={{ mt: 1 }}
                value={auswahl[0] ?? ''}
                onChange={event => waehle(event.target.value, false)}
              >
                {frage.AntwortArray.map(antwort => (
                  <FormControlLabel
                    key={antwort}
                    value={antwort}
                    control={<Radio size='small' />}
                    label={antwort}
                  />
                ))}
              </RadioGroup>
            ) : null}

            {frage.HinweisArray.length > 0 && (
              <Box sx={{ mt: 2 }}>
                {frage.HinweisArray.map(hinweis => (
                  <Accordion
                    key={hinweis.Titel}
                    disableGutters
                    elevation={0}
                    variant='outlined'
                  >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant='body2'>{hinweis.Titel}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ whiteSpace: 'pre-line' }}
                      >
                        {hinweis.Text}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}
          </>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant='subtitle2' sx={{ mb: 1 }}>
          Haben wir alles?
        </Typography>
        <GwGPflichtenListe
          pflichten={pruefung.Pflichten}
          hatAntworten={pruefung.AntwortArray.length > 0}
        />
      </DialogContent>

      <DialogActions>
        {vorletzte && (
          <Button
            startIcon={<Undo />}
            onClick={() => zurueckZu(vorletzte.NodeKey)}
            sx={{ mr: 'auto' }}
          >
            Eine Frage zurück
          </Button>
        )}
        {/* Der Katalog sagt es selbst: das Ergebnis gehört ausgedruckt in
            die Akte. Hier wandert es ohne Drucker dorthin. */}
        {frage === null && (
          <Button
            startIcon={<Save />}
            disabled={isPendingErgebnis}
            onClick={legeErgebnisAb}
          >
            Ergebnis in die Akte legen
          </Button>
        )}
        <Button onClick={onClose}>
          {frage === null ? 'Schließen' : 'Später fortsetzen'}
        </Button>
        {frage !== null && (
          <Button
            variant='contained'
            disabled={!istBeantwortbar || isPendingAntworten}
            onClick={() => weiter(frage)}
          >
            {frage.Art === 'Content' ? 'Weiter' : 'Antwort übernehmen'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
